import React from 'react';
import PropTypes from 'prop-types';
import Actions from 'components/ui/general/Actions';
import BookingsListFilters from 'pages/bookings/list/BookingsListFilters';
import CampaignField from 'components/field/CampaignField';
import ComputedPriceField from 'components/field/ComputedPriceField';
import DateField from 'components/field/DateFields/DateField';
import DateUTCField from 'components/field/DateFields/DateUTCField';
import DownloadCSV from 'pages/bookings/list/actions/csv/BookingCSVExport';
import FullNameField from 'components/field/FullNameField';
import Header from 'components/ui/general/Header';
import OrderStatusField from 'components/field/OrderStatusField';
import OrderSuspiciousField from 'components/field/OrderSuspiciousField';
import ProductField from 'components/field/ProductField';
import ResourceList from 'components/list/Container/ResourceList';
import Table from 'components/table/Table';
import TextField from 'components/field/TextField';
import Title from 'components/ui/general/Title';
import AccountField from 'components/field/AccountField';
import authHelper from 'helpers/auth';
import compose from 'recompose/compose';
import listQueryFilter from 'components/hoc/listQueryFilter';
import theme from 'theme/theme';
import translate from 'components/hoc/translate';
import { connect } from 'react-redux';
import { fetchBookings } from 'store/actions/bookings/fetchBookings';
import CreateButton from 'components/button/CreateButton';
import FullBookingCSVExport from './actions/csv/FullBookingCSVExport';

const BookingsList = (props) => {
    const {
        actionType,
        filter,
        location,
        resource,
        total,
        translate,
        updateQuery,
    } = props;

    const isAdmin = authHelper.isAdmin();
    const isProvider = authHelper.isProvider();
    const isReseller = authHelper.isReseller();

    return (
        <div>
            <Header>
                <Title title={ translate('resources.bookings.title') } />

                <Actions>
                    { isAdmin &&
                        <FullBookingCSVExport
                            base='created_at'
                            label={ translate('resources.orders.actions.export_related') }
                            page='orders'
                            filterValues={ filter }
                            resource='export'
                            total={ total }
                        />
                    }

                    <DownloadCSV
                        base='created_at'
                        label={ translate('resources.orders.actions.export') }
                        page='orders'
                        filterValues={ filter }
                        resource='export'
                        total={ total }
                    />

                    { isReseller &&
                        <CreateButton
                            basePath='booking-tool'
                            link='booking-tool'
                        />
                    }
                </Actions>
            </Header>

            <BookingsListFilters
                actionType={ actionType }
                location={ location }
                resource={ resource }
                updateQuery={ updateQuery }
            />

            <ResourceList
                action={ fetchBookings }
                actionType={ actionType }
                location={ location }
                resource={ resource }
                updateQuery={ updateQuery }
            >
                <Table
                    backgroundToggle={ {
                        condition: true,
                        color: theme.palette.colonialWhite,
                        key: 'suspicious',
                    } }
                >
                    <OrderSuspiciousField
                        column={ {
                            sortable: false,
                            width: '30px',
                        } }
                    />

                    <TextField
                        label='resources.bookings.fields.orderId'
                        source='order_id'
                        column={ {
                            sortable: false,
                            width: '125px',
                        } }
                    />

                    <DateUTCField
                        column={ {
                            sortable: true,
                            width: '150px',
                        } }
                        label='resources.bookings.fields.createdAt'
                        source='created_at'
                    />

                    <DateField
                        column={ {
                            sortable: true,
                            width: '150px',
                        } }
                        label='resources.bookings.fields.destinationArrivalDate'
                        source='destination_arrival_date'
                    />

                    { isAdmin &&
                        <AccountField
                            column={ {
                                link: false,
                                sortable: false,
                                width: '200px',
                            } }
                            display='name'
                            label='resources.bookings.fields.resellerId'
                            source='reseller_id'
                        />
                    }

                    { !isProvider &&
                        <CampaignField
                            display='name'
                            label='resources.bookings.fields.funnelId'
                            source='funnel_id'
                            column={ {
                                sortable: false,
                                width: '200px',
                            } }
                        />
                    }

                    <FullNameField
                        label='resources.bookings.fields.fullName'
                        column={ {
                            sortable: false,
                            width: '200px',
                        } }
                    />

                    { (isAdmin || isProvider) ?
                        <ProductField
                            column={ {
                                sortable: false,
                            } }
                            label='resources.bookings.fields.product'
                            source='product_id'
                        />
                        :
                        <TextField
                            column={ {
                                sortable: false,
                                width: '200px',
                            } }
                            label='resources.bookings.fields.product'
                            maxCharacters={ 100 }
                            source='product_info.title'
                        />
                    }

                    <OrderStatusField
                        column={ {
                            sortable: false,
                            width: '100px',
                        } }
                        label='resources.bookings.fields.status'
                        sortable={ false }
                        style={ { overflow: 'visible', padding: '0px' } }
                    />

                    { isReseller &&
                        <ComputedPriceField
                            source='reseller_base_price'
                            label='resources.bookings.fields.price'
                            column={ {
                                align: 'center',
                                sortable: false,
                                width: '100px',
                            } }
                        />
                    }

                    { isProvider &&
                        <ComputedPriceField
                            source='provider_total_price'
                            label='resources.bookings.fields.price'
                            column={ {
                                align: 'center',
                                sortable: false,
                                width: '100px',
                            } }
                        />
                    }

                    { isAdmin &&
                        <ComputedPriceField
                            source='amount'
                            label='resources.bookings.fields.price'
                            column={ {
                                align: 'center',
                                sortable: false,
                                width: '100px',
                            } }
                        />
                    }
                </Table>
            </ResourceList>
        </div>
    );
};

BookingsList.propTypes = {
    actionType: PropTypes.string.isRequired,
    filter: PropTypes.object,
    location: PropTypes.object,
    resource: PropTypes.string.isRequired,
    total: PropTypes.number,
    translate: PropTypes.func.isRequired,
    updateQuery: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    return {
        filter: state.bookings.filter,
        total: state.bookings.total,
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
        null,
    ),
    listQueryFilter,
    translate,
);

export default enhance(BookingsList);



// WEBPACK FOOTER //
// ./src/pages/bookings/list/BookingsList.js